import { useFormikContext } from 'formik';
import { CheckCircle2, FileUp, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { BatchCallFormValues } from '@/schemas/batch-call.schema';

export const RecipientsReview = () => {
  const { values } = useFormikContext<BatchCallFormValues>();
  const { recipients } = values;

  const validCount = recipients.filter(r => r.status === 'valid').length;
  const invalidCount = recipients.filter(r => r.status === 'invalid').length;

  return (
    <Card className="bg-muted rounded-2xl p-1 pt-0 gap-0 border-0 shadow-none flex flex-col">
      <CardHeader className="flex flex-col px-5 py-3">
        <CardTitle className="text-sm">Recipients Review</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 bg-card p-6 rounded-xl shadow-sm flex-1">
        <div className="mb-4">
          {recipients.length > 0 ? (
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-zinc-500">
                  Valid: <span className="font-medium text-zinc-950">{validCount}</span>
                </span>
              </div>
              {invalidCount > 0 && (
                <div className="flex items-center gap-1">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-zinc-500">
                    Invalid: <span className="font-medium text-red-600">{invalidCount}</span>
                  </span>
                </div>
              )}
              <div className="text-zinc-500">
                Total: <span className="font-medium text-zinc-950">{recipients.length}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No recipients uploaded yet</p>
          )}
        </div>

        {recipients.length > 0 ? (
          <div className="flex-1 overflow-y-auto border rounded-lg border-zinc-200">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead className="text-xs w-12">#</TableHead>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Phone Number</TableHead>
                  <TableHead className="text-xs">Metadata</TableHead>
                  <TableHead className="text-xs w-24">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.map((recipient, index) => (
                  <TableRow key={recipient.phoneNumber || `recipient-${index}`}>
                    <TableCell className="text-sm text-zinc-500">{index + 1}</TableCell>
                    <TableCell className="font-medium text-sm">{recipient.name}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{recipient.phoneNumber}</TableCell>
                    <TableCell className="text-sm text-zinc-600">
                      {JSON.stringify(recipient?.metadata) || '{}'}
                    </TableCell>
                    <TableCell>
                      {recipient.status === 'valid' ? (
                        <Badge
                          variant="outline"
                          className="text-green-600 border-green-200 bg-green-50 gap-1"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Valid
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-red-600 border-red-200 bg-red-50 gap-1"
                        >
                          <XCircle className="h-3 w-3" />
                          Invalid
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center border-2 border-dashed border-zinc-200 rounded-lg">
            <div className="text-center text-zinc-500 p-8">
              <FileUp className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
              <p className="text-sm font-medium">Upload a file to see recipients</p>
              <p className="text-xs text-zinc-400 mt-1">
                CSV files with Name and Phone Number columns
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
